import PermissionButton from "../../components/ui/PermissionButton";
import { useEffect, useState } from "react";
import { X } from "lucide-react";
import FinancialOwnership from '../../components/ui/FinancialOwnership';

export default function AddExpenseModal({
  expense,
  onClose,
}) {
  const [categories, setCategories] = useState([]);
  const [branches, setBranches] = useState([]);

  const [form, setForm] = useState({
    department_id:'',
    expense_date: new Date().toISOString().substring(0, 10),
    category_id: "",
    vendor_name: "",
    branch_id: "",
    payment_mode: "Cash",
    amount: "",
    reference: "",
    notes: "",
    attachment: "",
  });

  useEffect(() => {
    loadMasters();
  }, []);

  useEffect(() => {
    if (expense) {
      setForm({
        department_id:expense.department_id||'',
        expense_date: expense.expense_date,
        category_id: expense.category_id,
        vendor_name: expense.vendor_name,
        branch_id: expense.branch_id || "",
        payment_mode: expense.payment_mode,
        amount: expense.amount,
        reference: expense.reference || "",
        notes: expense.notes || "",
        attachment: expense.attachment || "",
      });
    }
  }, [expense]);

  async function loadMasters() {
    try {
      const [catRes, branchRes] = await Promise.all([
        fetch("/api/expenses/categories", {
          credentials: "include",
        }),
        fetch("/api/branches", {
          credentials: "include",
        }),
      ]);

      const catData = await catRes.json();
      const branchData = await branchRes.json();

      if (catData.status === "success") {
        setCategories(catData.data);
      }

      if (branchData.status === "success") {
        setBranches(branchData.data);
      }
    } catch (err) {
      console.log(err);
    }
  }

  const update = (field, value) => {
    setForm((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  async function submit() {
    if (!form.category_id) {
      return alert("Select category");
    }

    if (!form.amount || Number(form.amount) <= 0) {
      return alert("Enter valid amount");
    }

    const url = expense
      ? `/api/expenses/${expense.id}`
      : "/api/expenses";

    const method = expense ? "PUT" : "POST";

    const res = await fetch(url, {
      method,
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(form),
    });

    const data = await res.json();

    if (data.status === "success") {
      onClose();
    } else {
      alert(data.message);
    }
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center">

      <div className="w-full max-w-3xl rounded-2xl bg-[#09090B] border border-white/10">

        <div className="flex justify-between items-center px-6 py-5 border-b border-white/10">
          <div>
            <h2 className="text-2xl font-serif">
              {expense ? "Edit Expense" : "New Expense"}
            </h2>

            <p className="text-xs text-gray-400">
              Record business expenses
            </p>
          </div>

          <PermissionButton permissionModule="expenses"
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-white/10"
          >
            <X size={18} />
          </PermissionButton>
        </div>

        <div className="p-6 space-y-6">
          <FinancialOwnership branchId={form.branch_id} departmentId={form.department_id} locked={Boolean(expense?.department_id)} showBranch={false} onChange={values=>setForm(prev=>({...prev,...values}))}/>

          <div className="grid md:grid-cols-2 gap-5">

            <Input
              label="Expense Date"
              type="date"
              value={form.expense_date}
              onChange={(v) => update("expense_date", v)}
            />

            <div>
              <label className="text-xs text-gray-400 mb-2 block">
                Category
              </label>

              <select
                value={form.category_id}
                onChange={(e) =>
                  update("category_id", e.target.value)
                }
                className="w-full rounded-xl bg-black/40 border border-white/10 px-4 py-3"
              >
                <option value="">Select Category</option>

                {categories.map((c) => (
                  <option
                    key={c.id}
                    value={c.id}
                  >
                    {c.name}
                  </option>
                ))}
              </select>
            </div>

            <Input
              label="Vendor Name"
              value={form.vendor_name}
              onChange={(v) =>
                update("vendor_name", v)
              }
            />

            <Input
              label="Amount"
              type="number"
              value={form.amount}
              onChange={(v) =>
                update("amount", v)
              }
            />

            <div>
              <label className="text-xs text-gray-400 mb-2 block">
                Payment Mode
              </label>

              <select
                value={form.payment_mode}
                onChange={(e) =>
                  update("payment_mode", e.target.value)
                }
                className="w-full rounded-xl bg-black/40 border border-white/10 px-4 py-3"
              >
                <option>Cash</option>
                <option>Bank</option>
                <option>UPI</option>
                <option>Cheque</option>
              </select>
            </div>

            <div>
              <label className="text-xs text-gray-400 mb-2 block">
                Branch
              </label>

              <select
                value={form.branch_id}
                onChange={(e) =>
                  update("branch_id", e.target.value)
                }
                className="w-full rounded-xl bg-black/40 border border-white/10 px-4 py-3"
              >
                <option value="">Select Branch</option>

                {branches.map((b) => (
                  <option
                    key={b.id}
                    value={b.id}
                  >
                    {b.name}
                  </option>
                ))}
              </select>
            </div>

            <Input
              label="Reference"
              value={form.reference}
              onChange={(v) =>
                update("reference", v)
              }
            />

            <Input
              label="Attachment"
              value={form.attachment}
              onChange={(v) =>
                update("attachment", v)
              }
            />

          </div>

          <div>
            <label className="text-xs text-gray-400 mb-2 block">
              Notes
            </label>

            <textarea
              rows={4}
              value={form.notes}
              onChange={(e) =>
                update("notes", e.target.value)
              }
              className="w-full rounded-xl bg-black/40 border border-white/10 px-4 py-3"
            />
          </div>

        </div>

        <div className="flex justify-end gap-3 px-6 py-5 border-t border-white/10">

          <PermissionButton permissionModule="expenses"
            onClick={onClose}
            className="px-6 py-2 rounded-xl border border-white/10"
          >
            Cancel
          </PermissionButton>

          <PermissionButton permissionModule="expenses"
            onClick={submit}
            className="px-6 py-2 rounded-xl bg-amber-400 text-black"
          >
            {expense ? "Update Expense" : "Save Expense"}
          </PermissionButton>

        </div>

      </div>

    </div>
  );
}

function Input({
  label,
  value,
  onChange,
  type = "text",
}) {
  return (
    <div>
      <label className="text-xs text-gray-400 mb-2 block">
        {label}
      </label>

      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full rounded-xl bg-black/40 border border-white/10 px-4 py-3"
      />
    </div>
  );
}
